import { Link } from "react-router-dom";
import { useSeo } from "../lib/seo";
import {
  ArrowRight,
  Boxes,
  Building2,
  Eye,
  Server,
  ShieldCheck,
  Sparkles,
  Store,
  Target,
  Zap,
} from "lucide-react";
import { Badge } from "../components/ui/Badge";
import { buttonVariants } from "../components/ui/Button";
import { Reveal } from "../components/Reveal";

const PRINCIPLES = [
  {
    icon: Sparkles,
    title: "AI-native, not bolted on",
    body: "The platform is built around an agent that reads, writes, and reasons over your whole stack — connectors, workflows, and cross-system queries all flow from plain language.",
  },
  {
    icon: ShieldCheck,
    title: "Secure by architecture",
    body: "AI-generated code runs only inside an isolated gVisor sandbox, secrets live in a vault, and every configuration is Ed25519-signed. Safety is how the platform runs, not a policy on top.",
  },
  {
    icon: Server,
    title: "Portable, and yours",
    body: "Every connection is a signed, self-contained artifact that runs the same on-premise, in your private cloud, or as containers. Your data stays inside your boundary. No lock-in.",
  },
  {
    icon: Zap,
    title: "Minutes, not months",
    body: "Describe the outcome; the AI does the wiring; the sandbox proves it. What used to be a six-week integration project becomes a few minutes of conversation.",
  },
  {
    icon: Eye,
    title: "Humans stay in control",
    body: "Nothing goes live unverified. Every connector ships with tests, a readable config, and an audit trail — you review and approve before it touches a real system.",
  },
  {
    icon: Store,
    title: "Better together",
    body: "Build a connector once and share it across every workspace through the Connector Exchange — the schema travels, your secrets never do.",
  },
];

export default function About() {
  useSeo({
    title: 'About Shineywise Technologies',
    description: 'Shineywise Technologies builds Wise 2.0, an AI-powered enterprise integration platform that lets teams connect, automate, and query every business system in plain English.',
    path: '/about',
  });
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0 bg-aurora opacity-60" />
        <div className="pointer-events-none absolute -left-24 -top-16 h-72 w-72 rounded-full bg-accent-600/25 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <Badge tone="violet" className="mx-auto border border-white/10 bg-white/10 text-accent-200">
              <Building2 className="h-3.5 w-3.5" /> About Shineywise
            </Badge>
            <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl">
              Enterprise integration,{" "}
              <span className="bg-gradient-to-r from-sky-300 via-accent-300 to-accent-400 bg-clip-text text-transparent">
                reimagined for the AI era.
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              Wise 2.0 is an AI-native integration platform. Describe a system in plain English and it
              builds, tests, signs, and monitors the connector for you — so connecting enterprise
              systems takes minutes, not months.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          <Reveal>
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-glow">
              <Target className="h-6 w-6" />
            </span>
            <h2 className="mt-6 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Our mission</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-slate-600">
              To make every enterprise system speak the same language — without the glue code, the
              specialist teams, or the multi-month projects. Integration should be something anyone on
              the team can do by describing what they want.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Why we built it */}
      <section className="mx-auto max-w-4xl px-6 py-20">
        <div className="grid gap-10 md:grid-cols-[200px_1fr]">
          <Reveal>
            <h2 className="text-xl font-bold tracking-tight text-slate-900">Why we built it</h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="space-y-4 text-[15px] leading-relaxed text-slate-600">
              <p>
                Integration has been stuck for a decade. Connecting two systems still means expensive
                middleware, brittle custom code, security reviews, and a project plan measured in weeks.
                The systems changed, the tools didn't.
              </p>
              <p>
                Meanwhile, AI can now read an API's shape and write a working, tested connector. The
                missing piece was doing it <span className="font-medium text-slate-800">safely</span> —
                untrusted generated code can't be allowed near production systems or credentials.
              </p>
              <p>
                So we built Wise 2.0 around that constraint: the AI drafts connectors, but they only ever
                run in an isolated sandbox, must pass their own tests, and are signed before they go live.
                The result is the speed of AI with the guardrails an enterprise actually needs.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Principles */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <Badge tone="brand" className="mx-auto"><Sparkles className="h-3.5 w-3.5" /> What we believe</Badge>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Principles we build on
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PRINCIPLES.map(({ icon: Icon, title, body }, i) => (
              <Reveal key={title} delay={(i % 3) * 90}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-card transition-all hover:-translate-y-1 hover:shadow-lift">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100 transition-all duration-300 group-hover:bg-brand-gradient group-hover:text-white group-hover:shadow-glow">
                    <Icon className="h-6 w-6 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Company */}
      <section className="mx-auto max-w-4xl px-6 py-20">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-8 sm:p-10">
            <div className="flex items-center gap-3">
              <Boxes className="h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">The company</h2>
            </div>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
              Wise 2.0 is built and operated by Shineywise — a two-entity company spanning India and the
              United States. We're early and focused: partnering closely with our first design teams to
              get the platform right before we scale.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                "Shineywise Technologies OPC Pvt. Ltd.",
                "Shineywise Technologies LLC",
              ].map((e) => (
                <div key={e} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">{e}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-slate-500">
              Want to talk to us?{" "}
              <Link to="/contact" className="font-medium text-brand-700 hover:underline">Get in touch</Link>
              {" "}or email{" "}
              <a href="mailto:info@shineywise.com" className="font-medium text-brand-700 hover:underline">info@shineywise.com</a>.
            </p>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-slate-950 py-20">
        <div className="pointer-events-none absolute inset-0 bg-aurora opacity-60" />
        <div className="pointer-events-none absolute -right-16 -top-10 h-72 w-72 animate-blob rounded-full bg-accent-500/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-72 w-72 animate-blob rounded-full bg-brand-400/20 blur-3xl [animation-delay:3s]" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">See it for yourself</h2>
            <p className="mx-auto mt-4 max-w-lg text-lg text-slate-300">
              Spin up a free pilot workspace and connect your first system in minutes.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/signup" className={buttonVariants({ size: "lg", className: "group" })}>
                Start free <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" className="inline-flex h-12 items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 text-base font-semibold text-white backdrop-blur transition hover:bg-white/10">
                Contact us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
